// English comments only inside code.
// Centralized image URL builder hooks for the app.
// Safe change: only prefixes relative paths with VITE_BASE_IMAGE_SRC.
// Absolute URLs (http/https/data/blob) remain untouched.

import {buildImageSrc} from '@app/config/images';

// Keep a conservative size type so existing imports remain valid.
// We will not transform URLs based on size unless the incoming URL
// explicitly contains a "{size}" token (backward compatible).
export type ImageSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

type UseImageSrcOptions = {
  size?: ImageSize;
};

/**
 * Replace a "{size}" placeholder if present.
 * If the URL doesn't contain that placeholder, we do nothing.
 */
function applySizeToken(url: string, size?: ImageSize): string {
  if (!size) return url;
  if (url.includes('{size}')) {
    return url.replace('{size}', size);
  }
  return url;
}

/**
 * Hook: useImageSrc
 * - Accepts a possibly-relative path or an absolute URL.
 * - If relative, prefixes with VITE_BASE_IMAGE_SRC (from build-time env).
 * - If absolute (http/https/data/blob), returns as-is.
 * - Optionally replaces "{size}" token if present in the original URL.
 */
export function useImageSrc(
  initialSrc?: string | null,
  opts?: UseImageSrcOptions
): string | undefined {
  if (!initialSrc) return undefined;

  // First, apply optional size token (no-ops if missing).
  const sized = applySizeToken(String(initialSrc), opts?.size);

  // Then, build absolute URL using our centralized helper.
  const finalUrl = buildImageSrc(sized);

  // If buildImageSrc returned empty string (no base and empty input),
  // normalize to undefined so React omits the attribute.
  return finalUrl || undefined;
}

/**
 * Hook: useImageSrcSet
 * - For now, conservative behavior: if caller provides a URL that already
 *   contains a "{size}" token, we generate a simple srcset by replacing
 *   with a few sizes. Otherwise, we return undefined so React omits srcSet.
 * - This avoids making incorrect assumptions about server-side resizing.
 */
export function useImageSrcSet(src?: string | null): string | undefined {
  if (!src) return undefined;

  const input = String(src);
  if (!input.includes('{size}')) {
    // No size token, can't build a meaningful srcset safely.
    return undefined;
  }

  // Common, lightweight size buckets (can be adjusted if you have a known scheme).
  const sizes: ImageSize[] = ['sm', 'md', 'lg', 'xl'];

  const entries: string[] = [];
  for (const s of sizes) {
    const sized = applySizeToken(input, s);
    // Ensure each entry is absolute using the same helper.
    const abs = buildImageSrc(sized);
    if (abs) {
      // No width descriptors since scheme is token-based and unknown;
      // browsers still accept comma-separated URLs without descriptors,
      // but it's better to provide width hints if you have them.
      entries.push(abs);
    }
  }

  if (entries.length === 0) return undefined;
  return entries.join(', ');
}
